import styles from './CSS/UsersContent.module.css';
import React, { useMemo, useState } from 'react';
import { useUsers } from '../../../hooks/admin/useAllUsers';
import Loading from '../../../common/HousGlass Loader/Loading';
import UserContentForm from './UserContentForm';
import { X } from 'lucide-react';


function UsersContent() {
  const { data: users, isLoading, error, deleteUser } = useUsers();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [viewUser, setViewUser]=useState(false)
  
  const itemsPerPage = 10;

  // Calculate dashboard stats
  const stats = useMemo(() => {
    if (!users) return { total: 0, active: 0, Inactive: 0, verified: 0 };
    return {
      total: users.length,
      active: users.filter(user => user.admin_profile?.is_active).length,
      Inactive: users.filter(user => !user.admin_profile?.is_active).length,
      verified: users.filter(user => user.admin_profile?.is_verified).length
    };
  }, [users]);

  // Filter users based on search term and role
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    return users.filter(user => {
      const matchesSearch = 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = selectedRole === 'All Roles' || user.role === selectedRole.toLowerCase();
      
      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, selectedRole]);

  // Pagination
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(userId);
      } catch (error) {
        console.error('Failed to delete user:', error);
      }
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === paginatedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(paginatedUsers.map(user => user.id));
    }
  };

  const getUserStatus = (user) => {
    if (!user.admin_profile) return 'Pending';
    return user.admin_profile.is_active ? 'Active' : 'Inactive';
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Active': return styles.statusActive;
      case 'Inactive': return styles.statusInactive;
      case 'Pending': return styles.statusPending;
      default: return '';
    }
  };

  const getRoleClass = (role) => {
    switch (role.toLowerCase()) {
      case 'admin': return styles.roleAdmin;
      case 'editor': return styles.roleEditor;
      case 'user': return styles.roleUser;
      default: return '';
    }
  };


  if (isLoading) return <Loading />;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
      <div>
        <h1 className={styles.title}>User Management</h1>
        <p className={styles.subtitle}>Manage system users and their permissions</p>
        </div>
        <button className={styles.addButton} onClick={()=>setViewUser(true)}>Add New User</button>
      </div>

      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" className={styles.totalIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Total Users</span>
            <span className={styles.statValue}>{stats.total}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" className={styles.activeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Active Users</span>
            <span className={styles.statValue}>{stats.active}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" className={styles.InactiveIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
            </svg>
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Inactive</span>
            <span className={styles.statValue}>{stats.Inactive}</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <svg xmlns="http://www.w3.org/2000/svg" className={styles.verifiedIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statLabel}>Verified</span>
            <span className={styles.statValue}>{stats.verified}</span>
          </div>
        </div>
      </div>

      <div className={styles.tableControls}>
        <div className={styles.searchBar}>
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.filters}>
          <select
            className={styles.roleFilter}
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
          >
            <option>All Roles</option>
            <option>Admin</option>
            <option>super_admin</option>
            <option>User</option>
          </select>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedUsers.length === paginatedUsers.length && paginatedUsers.length > 0}
                  onChange={toggleAllUsers}
                />
              </th>
              <th className={styles.userColumn}>USER</th>
              <th>ROLE</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => toggleUserSelection(user.id)}
                  />
                </td>
                <td className={styles.userCell}>
                  <div className={styles.userInfo}>
                    <div className={styles.avatar}>
                      <img
                        src={user.admin_profile?.profile_image || "/api/placeholder/40/40"}
                        alt={user.name}
                        className={styles.userAvatar}
                      />
                    </div>
                    <div className={styles.userDetails}>
                      <div className={styles.userName}>{user.name}</div>
                      <div className={styles.userEmail}>{user.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`${styles.roleTag} ${getRoleClass(user.role)}`}>
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </span>
                </td>
                <td>
                  <span className={`${styles.statusTag} ${getStatusClass(getUserStatus(user))}`}>
                    {getUserStatus(user)}
                  </span>
                </td>
                <td className={styles.actions}>
                  <button 
                    className={styles.deleteButton}
                    onClick={() => handleDelete(user.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <span className={styles.pageInfo}>
          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
        </span>
        <div className={styles.pageControls}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={styles.pageButton}
          >
            Previous
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            const pageNum = i + 1;
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`${styles.pageButton} ${currentPage === pageNum ? styles.activePage : ''}`}
              >
                {pageNum}
              </button>
            );
          })}
          {totalPages > 5 && <span className={styles.ellipsis}>...</span>}
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={styles.pageButton}
          >
            Next
          </button>
        </div>
      </div>
      
        {viewUser&&
        <div className={styles.modalWrapper}>
          <div className={styles.modalContent}>
            <button className={styles.closeButton} onClick={() => setViewUser(false)}>
              <X />
            </button>
            <UserContentForm/>
          </div>
        </div>}

    </div>
  );
}


export default UsersContent;